import CardsStrategy from "../ListSourceWidget/strategies/CardsStrategy.js";
import ListSourceWidget from "../ListSourceWidget/ListSourceWidget.js";
import withDragAndDrop from "../ListSourceWidget/strategies/withDragAndDrop.js";

/**
 * Function initializing a {@link ListSourceWidget} with Cards strategy, with
 * drag'n'drop support.
 *
 * @param {ListSource} source - source of truth when it comes to
 *   fetching or manipulating the items
 * @param {function} itemRenderer - Function taking one fetched item
 *   as argument, and returning a Renderable for a card
 * @param {Renderable} [loadingCard] - Optional widget to display as
 *   card when loading
 * @param {Renderable} [trailingWidget] - Optional trailing widget to
 *   append at the end of the list of items
 * @param {string} [cssClass] - Additional CSS class to append to the
 *   root of the widget
 * @param {object} [dragAndDrop] - Options related to the drag and drop feature
 * @param {function} [dragAndDrop.isDraggable] - Optional predicate invoked with an item and its index in the list of items.
 *   When this function return `true`, support for drag is added to this item
 * @param {function} [dragAndDrop.isDroppable] - Optional predicate invoked with an item and its index in the list of items.
 *   When this function return `true`, support for drop is added to this item
 * @param {function} [dragAndDrop.onManualSort] - Optional callback to invoke when sortable items are sorted.
 *   This function is invoked with the list of sortable items, in the order chosen by the user
 * @param {function(DOMElement):DOMElement} [dragAndDrop.getDragHandle] - When
 *   provided, narrow down the drag handle to the result of the function. This
 *   function is invoked with the Card DOMElement
 */
export default function makeCardListWidget({
	source,
	itemRenderer,
	loadingCard,
	trailingWidget,
	cssClass,
	dragAndDrop: {
		isDraggable,
		isDroppable,
		onManualSort = () => {},
		getDragHandle,
	} = {},
}) {
	let strategy = new CardsStrategy({
		itemRenderer,
		loadingCard,
		trailingWidget,
		cssClass,
	});

	if (isDraggable ?? isDroppable) {
		strategy = withDragAndDrop(strategy, {
			isDraggable,
			isDroppable,
			onChange: onManualSort,
			getDragHandle,
		});
	}

	return new ListSourceWidget({ strategy, source });
}
